import { Injectable } from '@angular/core';
import { BehaviorSubject, map, distinctUntilChanged, combineLatest, Subject } from 'rxjs';
import { FLAGS } from 'src/app/services/flags.service';
import { DeviceAndBrowserDetectorService } from 'src/app/services/device-and-browser-detector.service';
import { FlagsService } from 'src/app/services/flags.service';
import { SpaceLeaderModeService } from 'src/app/services/space-leader-mode.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ProviderStateService } from 'src/app/services/provider-state.service';
import { AnalyticsEventsWindowEmitter } from 'src/app/services/analytics-events-window-emitter.service';
import { environment } from 'src/environments/environment';
import { RtcServiceController } from 'src/app/services/rtc.service';
import { TrackType } from 'src/app/common/interfaces/rtc-interface';
import { FollowModeUiService } from 'src/app/services/leader-follow-modes/follow-mode/follow-mode-ui.service';

export enum PIPState {
  INACTIVE = 'inactive',
  MANUAL_ACTIVE = 'manual_active',
  AUTO_ACTIVE = 'auto_active',
}

@UntilDestroy()
@Injectable()
export class PictureInPictureService {
  private _pipState = new BehaviorSubject<PIPState>(PIPState.INACTIVE);

  // Derived observables for backward compatibility and convenience
  isPIPActive$ = this._pipState.pipe(
    map((state) => state !== PIPState.INACTIVE),
    distinctUntilChanged(),
  );

  pipElement: Element | null = null;

  // Core tracking properties for video PiP
  private currentPiPParticipantId: string | null = null;
  private pipCloseHandler: (() => void) | null = null;

  // Observable for close events
  private _videoPiPClosed = new Subject<string>();
  videoPiPClosed$ = this._videoPiPClosed.asObservable();

  // Emits synchronously before moving the call container into the PiP document.
  private readonly _beforePIPElementMove = new Subject<void>();
  readonly beforePIPElementMove$ = this._beforePIPElementMove.asObservable();

  private readonly _pipOpenFailed = new Subject<void>();
  readonly pipOpenFailed$ = this._pipOpenFailed.asObservable();

  disabled$ = this.spaceLeaderModeService.currentUserIsBeingLed$.pipe(
    map(() => this.disabled),
    distinctUntilChanged(),
  );

  private featureEnabled =
    this.flagsService.isFlagEnabled(FLAGS.ENABLE_VIDEO_POP_OUT) &&
    this.deviceAndBrowserDetectorService.isDesktop();

  showPIPButton = !this.deviceAndBrowserDetectorService.isInIframe() && this.featureEnabled;

  parent: HTMLElement | null = null;

  readonly videoCallContainerIdSelector = 'app-wb-video-call-participant-id';
  readonly globalSvgSpriteId = 'global-svg-sprite';

  constructor(
    private flagsService: FlagsService,
    private deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private spaceLeaderModeService: SpaceLeaderModeService,
    private providerState: ProviderStateService,
    private embedAnalyticsEventsService: AnalyticsEventsWindowEmitter,
    private followModeService: FollowModeUiService,
  ) {
    // Handle media session action handler based on both call connection and user being led status
    if (this.isPopoutSupported && this.isAutoPopoutSupported) {
      try {
        combineLatest([
          this.providerState.callConnected$,
          this.spaceLeaderModeService.currentUserIsBeingLed$,
          this.followModeService.isFollowing$,
        ])
          .pipe(untilDestroyed(this))
          .subscribe(([callConnected, currentUserIsBeingLed, isFollowing]) => {
            if (
              callConnected &&
              this.flagsService.isFlagEnabled(FLAGS.ENABLE_AUTO_PICTURE_IN_PICTURE) &&
              !currentUserIsBeingLed &&
              !isFollowing
            ) {
              navigator.mediaSession.setActionHandler(
                'enterpictureinpicture' as MediaSessionAction,
                () => this.openPIP(true),
              );
            } else {
              navigator.mediaSession.setActionHandler(
                'enterpictureinpicture' as MediaSessionAction,
                null,
              );
              window.pipWindow?.close();
            }
          });
      } catch (error) {
        console.error('Error setting enterpictureinpicture action handler', error);
      }
    }
  }

  // call it on leaving the session component
  // TODO: provide the service in the app-wb-video-call-participant-id component and put the code in ngOnDestroy
  cleanupDocumentPiPState() {
    window.pipWindow?.close();
    this.setPIPWindow(undefined);
    this.pipElement = null;
    this.parent = null;
  }

  async openPIP(isAutoTriggered: boolean = false): Promise<void> {
    if (
      !this.isPopoutSupported ||
      !this.featureEnabled ||
      window.documentPictureInPicture?.window
    ) {
      console.log('PIP not supported or already open');
      return;
    }

    try {
      this.pipElement =
        this.pipElement || document.querySelector(this.videoCallContainerIdSelector);
      if (!this.pipElement) {
        throw new Error('PIP Element not found');
      }
      this.parent = this.parent || this.pipElement.parentElement;

      // Ensure main document has an inline sprite available for overlays (e.g., MatMenu)
      // as chrome new version not render the svg icons after opening and closing the pip window
      if (this.flagsService.isFlagEnabled(FLAGS.INLINE_SVG_SPRITE_MAIN_DOC)) {
        await this.ensureGlobalSpriteInMainDocument();
      }

      // Open a Picture-in-Picture window.
      const pipWindow = await window.documentPictureInPicture?.requestWindow();

      if (!pipWindow) {
        throw new Error('PIP window not supported -> documentPictureInPicture is undefined');
      }

      if (pipWindow.closed) {
        throw new Error('requested PIP window already closed');
      }

      if (window.pipWindow && !window.pipWindow.closed && window.pipWindow !== pipWindow) {
        throw new Error('there is already an active pip window');
      }

      // Copy style sheets over from the initial document
      // so that the element looks the same.
      this.copyStyleSheets(pipWindow);

      this._beforePIPElementMove.next();

      // Move the element to the Picture-in-Picture window.
      pipWindow.document.body.append(this.pipElement);

      // Copy SVG sprite to PIP window
      this.copySvgSpriteToPIP(pipWindow);

      this.setPIPWindow(pipWindow, isAutoTriggered);

      // Move the document back when the Picture-in-Picture window closes.
      this.moveElementBackWhenPIPCloses(pipWindow);

      this.embedAnalyticsEventsService.emitPiPToggle(true);
    } catch (error) {
      console.error('Error opening PIP', error);
      this._pipOpenFailed.next();
    }
  }

  closePIP(): void {
    if (this._pipState.value === PIPState.INACTIVE || !window.pipWindow) {
      console.log('PIP is not active or already closed');
      return;
    }

    try {
      // Close the PIP window - cleanup is handled by pagehide event listener
      window.pipWindow?.close();
    } catch (error) {
      console.error('Error closing PIP', error);
    }
  }

  get isPopoutSupported(): boolean {
    return (
      'documentPictureInPicture' in window && !this.deviceAndBrowserDetectorService.isInIframe()
    );
  }

  get isAutoPopoutSupported(): boolean {
    return this.deviceAndBrowserDetectorService.isChromeBrowserAbove90();
  }

  get isPIPActive(): boolean {
    return this._pipState.value !== PIPState.INACTIVE;
  }

  get disabled(): boolean {
    return this.spaceLeaderModeService.currentUserIsBeingLed() || !this.isPopoutSupported;
  }

  /**
   * Checks if the traditional video Picture-in-Picture API is supported by the browser
   * @returns true if video PIP is supported, false otherwise
   */
  public isVideoPictureInPictureSupported(): boolean {
    // Desktop only
    if (!this.deviceAndBrowserDetectorService.isDesktop()) {
      return false;
    }

    // Check if browser supports Picture-in-Picture API
    if (!('pictureInPictureEnabled' in document)) {
      return false;
    }

    // Firefox-specific handling: Firefox supports PIP but might return false
    // for document.pictureInPictureEnabled in some cases, so we do additional checks
    if (this.deviceAndBrowserDetectorService.isFirefox()) {
      // For Firefox, also check if HTMLVideoElement has requestPictureInPicture method
      // as an additional validation since Firefox's pictureInPictureEnabled can be unreliable
      return (
        'requestPictureInPicture' in HTMLVideoElement.prototype &&
        document.pictureInPictureEnabled !== false
      );
    }

    // For other browsers, use the standard check
    return document.pictureInPictureEnabled;
  }

  // Core method: Open PiP video
  public async openPipVideo(participantId: string): Promise<boolean> {
    try {
      // Close existing PiP if different participant
      if (this.currentPiPParticipantId && this.currentPiPParticipantId !== participantId) {
        await this.closePipVideo(this.currentPiPParticipantId);
      }

      const videoPlayer = RtcServiceController.getVideoElement(participantId, TrackType.VIDEO);
      if (!videoPlayer) {
        return false;
      }

      // Open PiP
      await videoPlayer.requestPictureInPicture();

      // Setup tracking and listener
      this.currentPiPParticipantId = participantId;
      this.setupCloseListener(videoPlayer, participantId);

      return true;
    } catch (error) {
      console.error('Error opening PIP video:', error);
      return false;
    }
  }

  // Core method: Close PiP video
  public async closePipVideo(participantId: string): Promise<void> {
    const videoPlayer = RtcServiceController.getVideoElement(participantId, TrackType.VIDEO);

    if (!videoPlayer) {
      return;
    }

    if (document.pictureInPictureElement === videoPlayer) {
      try {
        await document.exitPictureInPicture();
      } catch (error) {
        console.error('Error closing PiP video:', error);
      }
    }
  }

  // Setup close event listener
  private setupCloseListener(videoElement: HTMLVideoElement, participantId: string): void {
    // Clean up existing listener
    if (this.pipCloseHandler) {
      videoElement.removeEventListener('leavepictureinpicture', this.pipCloseHandler);
    }

    // Create and add new listener
    this.pipCloseHandler = () => {
      this.currentPiPParticipantId = null;
      this.pipCloseHandler = null;
      this._videoPiPClosed.next(participantId);
    };

    videoElement.addEventListener('leavepictureinpicture', this.pipCloseHandler);
  }

  // Cleanup on service destroy
  public async cleanupVideoPiP(): Promise<void> {
    if (this.currentPiPParticipantId) {
      await this.closePipVideo(this.currentPiPParticipantId);
    }
  }

  isPIPAutoTriggered(): boolean {
    return this._pipState.value === PIPState.AUTO_ACTIVE;
  }

  private setPIPWindow(pipWindow: Window | undefined, isAutoTriggered: boolean = false) {
    window.pipWindow = pipWindow;
    if (pipWindow) {
      this._pipState.next(isAutoTriggered ? PIPState.AUTO_ACTIVE : PIPState.MANUAL_ACTIVE);
    } else {
      this._pipState.next(PIPState.INACTIVE);
    }
  }

  private copyStyleSheets(pipWindow: Window) {
    [...(document.styleSheets as any)].forEach((styleSheet) => {
      try {
        const cssRules = [...styleSheet.cssRules].map((rule) => rule.cssText).join('');
        const style = document.createElement('style');
        style.textContent = cssRules;
        pipWindow.document.head.appendChild(style);
      } catch (e) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.type = styleSheet.type;
        link.media = styleSheet.media;
        link.href = styleSheet.href;
        pipWindow.document.head.appendChild(link);
      }
    });

    pipWindow.document.body.style.overflow = 'hidden';
  }

  private async copySvgSpriteToPIP(pipWindow: Window): Promise<void> {
    try {
      const spriteContent = await this.fetchSvgSprite();
      if (spriteContent) {
        // Create a hidden container for the SVG sprite in PIP window
        const spriteContainer = pipWindow.document.createElement('div');
        spriteContainer.style.display = 'none';
        spriteContainer.innerHTML = spriteContent;
        pipWindow.document.body.appendChild(spriteContainer);
      }
    } catch (error) {
      console.error('Error copying SVG sprite to PIP window:', error);
    }
  }

  private async ensureGlobalSpriteInMainDocument(): Promise<void> {
    try {
      if (document.getElementById(this.globalSvgSpriteId)) {
        return;
      }
      const spriteContent = await this.fetchSvgSprite();
      if (!spriteContent) {
        return;
      }
      const container = document.createElement('div');
      container.id = this.globalSvgSpriteId;
      container.style.display = 'none';
      container.innerHTML = spriteContent;
      // Place near end of body so it's globally available to overlays
      document.body.appendChild(container);
    } catch (error) {
      console.error('Error ensuring global SVG sprite in main document:', error);
    }
  }

  private async fetchSvgSprite(): Promise<string | null> {
    try {
      // Use absolute URL to ensure correct resolution from any context
      const spriteUrl = new URL(
        `assets/icons/svg-sprite/${environment.svgSpriteName}`,
        window.location.origin,
      ).href;
      const response = await fetch(spriteUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch SVG sprite: ${response.statusText}`);
      }

      const svgContent = await response.text();

      // Wrap the sprite content in a hidden SVG container if it's not already wrapped
      if (svgContent.includes('<symbol')) {
        return `<svg style="display: none;">${svgContent}</svg>`;
      }

      return svgContent;
    } catch (error) {
      console.error('Error fetching SVG sprite:', error);
      return null;
    }
  }

  private moveElementBackWhenPIPCloses(pipWindow: Window) {
    pipWindow.addEventListener('pagehide', () => {
      if (this._pipState.value === PIPState.INACTIVE || !window.pipWindow) {
        console.log('PIP is already closed');
        return;
      }

      if (!this.pipElement) {
        console.log('PIP player not found on pagehide');
        return;
      }
      this.parent?.prepend(this.pipElement);
      this.setPIPWindow(undefined);

      this.embedAnalyticsEventsService.emitPiPToggle(false);
    });
  }
}
